import { ArrowRightLeft } from 'lucide-react'
import type { ColorPreviewProps } from '../../types/contrast'

export default function ColorPreview({ foreground, background, onSwap }: ColorPreviewProps) {
  const isValid = foreground.isValid && background.isValid

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Live Preview</h3>
        <button
          type="button"
          onClick={onSwap}
          className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          aria-label="Swap foreground and background colors"
        >
          <ArrowRightLeft className="w-4 h-4 mr-1.5" />
          Swap Colors
        </button>
      </div>

      {/* Preview Box */}
      <div
        className="relative rounded-lg border-2 border-gray-200 overflow-hidden"
        style={{
          backgroundColor: isValid ? background.hex : '#FFFFFF'
        }}
      >
        {/* Normal Text Preview */}
        <div className="p-6">
          <p
            className="text-base mb-2"
            style={{ color: isValid ? foreground.hex : '#000000' }}
          >
            Normal text (16px) - The quick brown fox jumps over the lazy dog.
          </p>
          <p
            className="text-base font-bold mb-4"
            style={{ color: isValid ? foreground.hex : '#000000' }}
          >
            Normal text bold (16px) - The quick brown fox jumps over the lazy dog.
          </p>
          <p
            className="text-2xl mb-2"
            style={{ color: isValid ? foreground.hex : '#000000' }}
          >
            Large text (24px) - The quick brown fox jumps.
          </p>
          <p
            className="text-lg font-bold"
            style={{ color: isValid ? foreground.hex : '#000000' }}
          >
            Large text bold (18px+) - The quick brown fox jumps.
          </p>
        </div>

        {/* UI Component Preview */}
        <div
          className="p-4 border-t-2"
          style={{
            borderColor: isValid ? foreground.hex : '#000000',
            backgroundColor: isValid ? background.hex : '#FFFFFF'
          }}
        >
          <div className="flex items-center space-x-4">
            {/* Sample Button */}
            <button
              type="button"
              className="px-4 py-2 rounded-lg font-medium transition-colors"
              style={{
                backgroundColor: isValid ? foreground.hex : '#000000',
                color: isValid ? background.hex : '#FFFFFF'
              }}
            >
              Sample Button
            </button>

            {/* Sample Input */}
            <div
              className="px-4 py-2 rounded-lg border-2"
              style={{
                borderColor: isValid ? foreground.hex : '#000000',
                backgroundColor: isValid ? background.hex : '#FFFFFF',
                color: isValid ? foreground.hex : '#000000'
              }}
            >
              Input Field
            </div>

            {/* Sample Checkbox */}
            <div className="flex items-center space-x-2">
              <div
                className="w-5 h-5 rounded border-2 flex items-center justify-center"
                style={{
                  borderColor: isValid ? foreground.hex : '#000000',
                  backgroundColor: isValid ? background.hex : '#FFFFFF'
                }}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke={isValid ? foreground.hex : '#000000'}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span style={{ color: isValid ? foreground.hex : '#000000' }}>Checkbox</span>
            </div>
          </div>
        </div>

        {/* Color Info Bar */}
        <div className="flex border-t border-gray-200 text-xs">
          <div className="flex-1 p-2 text-center bg-gray-50">
            <span className="text-gray-500">Foreground:</span>{' '}
            <code className="font-mono">{foreground.hex || '—'}</code>
          </div>
          <div className="flex-1 p-2 text-center bg-gray-50 border-l border-gray-200">
            <span className="text-gray-500">Background:</span>{' '}
            <code className="font-mono">{background.hex || '—'}</code>
          </div>
        </div>
      </div>
    </div>
  )
}
